import { formatPhoneNumberAsTyped } from './phoneFormatting.ts'
import type { AccountIdentity } from './accountIdentity.ts'

/**
 * The name one account gives another.
 *
 * An alias is **private to whoever wrote it**. Fernando calling `sandra@ultramega.email`
 * "mãe" is a fact about Fernando's address book and about nothing else: it must never reach
 * Sandra, through a payload, a notification, or an error message. Everything here takes a
 * viewer for that reason, and the repository never returns an alias to anybody but its
 * owner.
 *
 * It is also not `users.display_name`. That column belongs to Google and is overwritten on
 * every sign-in; an alias belongs to a person and survives.
 */

/** Separators people write inside the local part of an address, standing in for spaces. */
const LOCAL_PART_SEPARATORS = /[._-]+/

function titleCaseWord(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1).toLowerCase()
}

/**
 * The name an address suggests: `fernando.reis.guimaraes@gmail.com` reads as
 * `Fernando Reis Guimaraes`. A first guess, and one the person renames in two clicks.
 */
export function buildAliasFromEmailAddress(emailAddress: string): string {
  const separatorPosition = emailAddress.lastIndexOf('@')
  const localPart = separatorPosition > 0 ? emailAddress.slice(0, separatorPosition) : emailAddress

  return localPart
    .split(LOCAL_PART_SEPARATORS)
    .filter((word) => word.length > 0)
    .map(titleCaseWord)
    .join(' ')
}

/**
 * The automatic alias for an account, from the address it is best known at.
 *
 * A number becomes the number, formatted the way its own country writes it, because that is
 * what the person recognizes. An account reachable only through a proof — a Google subject
 * and nothing else — has no address to read a name from, and gets none.
 */
export function buildAutomaticAlias(identities: readonly AccountIdentity[]): string | null {
  const emailIdentity = identities.find((identity) => identity.provider === 'email')
  if (emailIdentity !== undefined) {
    const aliasFromAddress = buildAliasFromEmailAddress(emailIdentity.subject)
    return aliasFromAddress.length > 0 ? aliasFromAddress : null
  }

  const phoneIdentity = identities.find((identity) => identity.provider === 'phone')
  if (phoneIdentity !== undefined) {
    return formatPhoneNumberAsTyped(phoneIdentity.subject)
  }

  return null
}

/**
 * How a person is named on screen: `Sandra <sandra@ultramega.email>`, the shape an email
 * client uses, because it answers both questions at once — who this is, and how to reach
 * them.
 *
 * Two contacts may carry the same alias and need no disambiguating, because the address is
 * right there beside it.
 */
export function describeAccountWithAlias(alias: string | null, handle: string): string {
  const trimmedAlias = alias?.trim() ?? ''
  return trimmedAlias.length === 0 ? handle : `${trimmedAlias} <${handle}>`
}

/**
 * The alias as it appears inside a URL: lowercase, unaccented, hyphenated.
 *
 * Decoration only. Nothing resolves it — the code beside it does — so it may repeat, it may
 * be renamed, and an old link carrying an old slug still arrives.
 */
export function slugifyAlias(alias: string): string {
  const withoutAccents = alias.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const slug = withoutAccents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  // A name written entirely in a script this cannot transliterate leaves nothing behind,
  // and a URL segment has to be something.
  return slug.length === 0 ? 'x' : slug
}

export const MAXIMUM_ALIAS_LENGTH = 80

/**
 * The name a person gave themselves, assembled from the two halves they typed.
 *
 * It outlives every provider, which is the whole reason it is not `display_name`: Google
 * rewrites that column on every sign-in, and somebody who corrects the spelling of their
 * own name should not have it corrected back.
 */
export function buildChosenName(
  firstName: string | null,
  lastName: string | null,
): string | null {
  const chosenName = [firstName ?? '', lastName ?? '']
    .map((namePart) => namePart.trim())
    .filter((namePart) => namePart.length > 0)
    .join(' ')
  return chosenName.length > 0 ? chosenName : null
}

/**
 * What an account is called when nobody has named it themselves: their own chosen name
 * first, then whatever a provider supplied, and otherwise nothing — the caller falls back
 * to the handle, which every account has.
 */
export function resolveAccountDisplayName(account: {
  firstName: string | null
  lastName: string | null
  displayName: string | null
}): string | null {
  return buildChosenName(account.firstName, account.lastName) ?? account.displayName
}

export const MAXIMUM_NAME_LENGTH = 60

/**
 * What one account is called on another's screen, once every source is weighed.
 *
 * The order is the whole design of naming in one function:
 *
 * 1. **A name the viewer chose.** Theirs, private, and it beats everything for good.
 * 2. **The name the subject gave themselves.** Better information about a person than
 *    their own address is, which is why it beats a derived alias.
 * 3. **The alias derived from their address**, which was only ever a placeholder.
 * 4. **Whatever a provider supplied**, which nobody here chose.
 *
 * `null` when none of them exists, and the caller falls back to the handle.
 */
export function resolveDisplayNameForViewer(
  aliasEntry: { alias: string; wasChosen: boolean } | null,
  subject: { firstName: string | null; lastName: string | null; displayName: string | null },
): string | null {
  if (aliasEntry?.wasChosen === true) {
    return aliasEntry.alias
  }
  return buildChosenName(subject.firstName, subject.lastName) ?? aliasEntry?.alias ?? subject.displayName
}
